app.controller('manualPaymentsV2Controller', function ($rootScope, $scope, $filter, safeApply, accountingService) {

  // Pulls in user info from the rootScope -> CRMController
  $rootScope.$watch('user_info', function () {
    $scope.user_info = $rootScope.user_info
  }, true)

  $scope.reconciling = false
  $scope.posting = false
  $scope.postProgress = 0

  // amount starts null, not 0, so a fresh row shows its placeholder instead of
  // a literal "0". Number(null) is 0, so the blank/total maths are unaffected.
  function blankRow() {
    return {
      user_id: '',
      check_number: '',
      amount: null,
      notes: '',
      customer_name: '',
      customer_email: '',
      interest: 0,
      amount_owed: 0,
      issues: [],
      reconciled: false,
      override: false,
      confirmRemove: false,
      confirmInterest: false,
      removingInterest: false,
      // Set once the payment is committed. A posted row is never posted again,
      // never re-editable, and is excluded from any later batch.
      group_size: 0,
      group_index: 0,
      group_total: 0,
      group_color: 0,
      posted: false,
      post_error: '',
      email_sent: false,
      email_error: ''
    }
  }

  // The register always carries one trailing blank row so the user can keep
  // typing without reaching for "Add Row".
  $scope.rows = [blankRow()]

  $scope.isBlank = function (row) {
    return !row.user_id && !row.check_number && !Number(row.amount)
  }

  $scope.enteredRows = function () {
    return $scope.rows.filter(function (row) { return !$scope.isBlank(row) })
  }

  $scope.totalCheckAmount = function () {
    return $scope.enteredRows().reduce(function (sum, row) {
      return sum + (Number(row.amount) || 0)
    }, 0)
  }

  $scope.reconciledCount = function () {
    return $scope.rows.filter(function (row) { return row.reconciled }).length
  }

  // Editing a row invalidates whatever the last reconcile pass put on it.
  $scope.rowChanged = function (row) {
    row.reconciled = false
    row.customer_name = ''
    row.customer_email = ''
    row.interest = 0
    row.amount_owed = 0
    row.issues = []
    row.override = false
    appendTrailingBlank()
    // Live, not deferred to the next Reconcile: a mistyped duplicate should be
    // visible while the operator still has the cheque in hand.
    refreshRowRelations()
  }

  function appendTrailingBlank() {
    const last = $scope.rows[$scope.rows.length - 1]
    if (!last || !$scope.isBlank(last)) $scope.rows.push(blankRow())
  }

  $scope.addRow = function () {
    $scope.rows.push(blankRow())
  }

  // Inline confirmations - only one popout is ever open, of either kind.
  function closeAllPopouts() {
    $scope.rows.forEach(function (r) {
      r.confirmRemove = false
      r.confirmInterest = false
    })
  }

  $scope.askRemove = function (row) {
    closeAllPopouts()
    row.confirmRemove = true
  }

  $scope.cancelRemove = function (row) {
    row.confirmRemove = false
  }

  $scope.askRemoveInterest = function (row) {
    closeAllPopouts()
    row.confirmInterest = true
  }

  $scope.cancelRemoveInterest = function (row) {
    row.confirmInterest = false
  }

  $scope.removeRow = function (index) {
    $scope.rows.splice(index, 1)
    if (!$scope.rows.length) $scope.rows.push(blankRow())
    // Removing the trailing blank would otherwise leave nowhere to type.
    appendTrailingBlank()
    // Deleting one half of a pair clears the flag on the other.
    refreshRowRelations()
  }

  // Issues ---------------------------------------------------------------------
  // One rule: any issue at all holds the row back until it is corrected or
  // overridden. Nothing posts unreviewed.
  //
  // "severity" only tints the chip - it does not change whether the row blocks.
  // "account" is a problem with the account itself, "amount" a variance between
  // the check and the balance due.
  //
  // overridable=false marks an issue the operator cannot wave through: no User
  // ID given, a User ID that resolves to no account, no amount, or a lookup that
  // failed and left the balance unknown. What remains overridable is the set
  // where the account and its balance are both known and the only question is
  // judgement - a variance, or a zero balance.
  function issue(text, severity, overridable) {
    return { text: text, severity: severity, overridable: overridable !== false }
  }

  function hasIssue(row) {
    return row.issues.length > 0
  }
  $scope.hasIssue = hasIssue

  // Offer Override only when every issue on the row can be waived.
  $scope.canOverride = function (row) {
    return hasIssue(row) && row.issues.every(function (i) { return i.overridable })
  }

  $scope.toggleOverride = function (row) {
    if (!$scope.canOverride(row)) return
    row.override = !row.override
  }

  // A row still carrying an unwaived issue.
  function isBlocked(row) {
    return hasIssue(row) && !row.override
  }
  $scope.isBlocked = isBlocked

  // Duplicates -----------------------------------------------------------------
  // The same check number against the same account, entered twice. Normalised
  // before comparing so "00412 " and "412" are recognised as the same check.
  const DUPLICATE_TEXT = 'Duplicate entry'

  function duplicateKey(row) {
    const id = String(row.user_id || '').trim().toLowerCase()
    const check = String(row.check_number || '').trim().toLowerCase().replace(/^0+/, '')
    return (id && check) ? id + '|' + check : ''
  }

  // Grouping is by account alone: several different checks from one customer.
  function accountKey(row) {
    return String(row.user_id || '').trim().toLowerCase()
  }

  // How many colours the register cycles through when marking groups. Must match
  // the .cr-grouped-c* / .cr-group-c* rules in _accounting.scss.
  const GROUP_COLORS = 6

  function isDuplicate(row) {
    const key = duplicateKey(row)
    if (!key) return false
    return $scope.rows.filter(function (r) { return duplicateKey(r) === key }).length > 1
  }

  /*
    A duplicate is a property of the pair, not of one row, so fixing either half
    has to clear the flag on the other. Rather than trying to patch the affected
    row, the whole register is re-evaluated on every edit - it is a handful of
    rows and this cannot drift out of step.

    Both halves are flagged, not just the later one, so the operator can see what
    collides with what. Posted rows count too: re-entering a check that already
    went out is exactly the mistake worth catching.
  */
  function refreshRowRelations() {
    $scope.rows.forEach(function (row) {
      const flagged = row.issues.some(function (i) { return i.text === DUPLICATE_TEXT })
      const duplicate = isDuplicate(row)
      if (duplicate && !flagged) {
        row.issues.unshift(issue(DUPLICATE_TEXT, 'account'))
      } else if (!duplicate && flagged) {
        row.issues = row.issues.filter(function (i) { return i.text !== DUPLICATE_TEXT })
      }
    })

    /*
      Grouping: several checks entered against one account. Computed here and
      stored on the row rather than as scope functions called from ng-repeat,
      which would re-scan the register for every row on every digest.

      Rows are deliberately NOT reordered - the operator is typing, and moving
      a row out from under the cursor to sit beside its group would be worse
      than labelling it in place.
    */
    const groups = {}
    const groupOrder = []
    $scope.enteredRows().forEach(function (row) {
      const key = accountKey(row)
      if (!key) return
      if (!groups[key]) {
        groups[key] = []
        groupOrder.push(key)
      }
      groups[key].push(row)
    })

    /*
      Each grouped account gets its own colour, so two groups a few rows apart
      are told apart at a glance instead of by reading "Check n of m".

      Slots are handed out in the order the groups first appear in the register,
      tracked in groupOrder rather than taken from Object.keys(groups): User IDs
      are integer-like keys, which JavaScript enumerates in numeric order, so
      keying off the object would re-colour the whole register the moment a
      lower ID was typed at the bottom.

      The palette cycles once it is exhausted. Two groups can then share a
      colour, but only with several groups between them, and the "Check n of m"
      label still carries the exact relationship.
    */
    let slot = 0
    const colorByKey = {}
    groupOrder.forEach(function (key) {
      if (groups[key].length > 1) colorByKey[key] = slot++ % GROUP_COLORS
    })

    $scope.rows.forEach(function (row) {
      const key = accountKey(row)
      const peers = groups[key] || []
      const grouped = peers.length > 1
      row.group_size = grouped ? peers.length : 0
      row.group_index = grouped ? peers.indexOf(row) + 1 : 0
      row.group_color = grouped ? colorByKey[key] : 0
      row.group_total = grouped
        ? peers.reduce(function (sum, p) { return sum + (Number(p.amount) || 0) }, 0)
        : 0
    })
  }

  // Money is compared in whole cents. Comparing the floats directly reports
  // phantom one-cent variances on values that cannot be held exactly in binary.
  function cents(value) {
    return Math.round((Number(value) || 0) * 100)
  }

  function money(value) {
    return $filter('currency')(value, '$', 2)
  }

  // The reconciliation rules, applied to one row against its customer record.
  function checkRow(row, customer) {
    row.issues = []

    // Raised before anything that depends on the lookup: a duplicate is visible
    // from the entered data alone, so it shows even on a row whose User ID does
    // not resolve.
    if (isDuplicate(row)) {
      row.issues.push(issue(DUPLICATE_TEXT, 'account'))
    }

    if (!customer) {
      row.issues.push(issue('User ID not found', 'account', false))
      return
    }

    const owed = cents(row.amount_owed)

    // Per row: an empty amount is this row's problem, not the account's.
    if (!cents(row.amount)) {
      row.issues.push(issue('No check amount entered', 'account', false))
      return
    }

    /*
      The variance is measured on the ACCOUNT, not on the check in isolation.
      Three checks of $500 settling a $1,500 balance are exact; comparing each
      one against the full balance would flag all three as "short by $1,000"
      and invite the operator to chase a shortfall that does not exist.

      Computed from the register here rather than read off row.group_total, so
      it cannot depend on refreshRowRelations having run first.
    */
    const peers = $scope.enteredRows().filter(function (r) {
      return accountKey(r) === accountKey(row)
    })
    const grouped = peers.length > 1
    const paid = cents(peers.reduce(function (sum, r) {
      return sum + (Number(r.amount) || 0)
    }, 0))

    // Raised alongside the amount checks rather than instead of them: an account
    // can be short-paid and also have nowhere to send the confirmation, and the
    // operator needs to see both. Overridable - the payment itself is fine, it
    // just posts without a receipt.
    if (!row.customer_email) {
      row.issues.push(issue('No Email Address', 'account'))
    }

    // Nothing outstanding: posting against this account would open a credit.
    if (owed <= 0) {
      row.issues.push(issue('No balance due', 'account'))
      return
    }

    // The wording names the account when several checks are being measured
    // together, so the figure is not mistaken for this one check's shortfall.
    const subject = grouped ? 'Account ' : ''
    if (paid > owed) {
      row.issues.push(issue(subject + (grouped ? 'over by ' : 'Over by ') + money((paid - owed) / 100), 'amount'))
    } else if (paid < owed) {
      row.issues.push(issue(subject + (grouped ? 'short by ' : 'Short by ') + money((owed - paid) / 100), 'amount'))
    }
  }

  // interest_applied comes from the GET_CLIENT_BILLING_LEDGER_GLENN_v3 proc. If
  // it is ever renamed there the Interest column would just read as zero for
  // everyone, so say so once per session rather than failing silently.
  let warned_no_interest_field = false

  async function lookupCustomer(user_id, cache) {
    if (cache && cache[user_id] !== undefined) return cache[user_id]
    const results = await accountingService.getGoldClients({ user_id: user_id })
    const customer = (results.data.success && results.data.data && results.data.data.length)
      ? results.data.data[0]
      : null
    if (cache) cache[user_id] = customer
    return customer
  }

  // Writes a looked-up customer record onto a row and re-runs the rules.
  function applyCustomer(row, customer) {
    if (!customer) {
      row.customer_name = ''
      row.customer_email = ''
      row.interest = 0
      row.amount_owed = 0
      row.reconciled = false
      checkRow(row, null)
      return
    }

    if (customer.interest_applied === undefined && !warned_no_interest_field) {
      warned_no_interest_field = true
      console.warn('reconcile: customer record has no interest_applied field - the Interest column will read as zero for every row', customer)
    }

    row.customer_name = [customer.first_name, customer.last_name].filter(Boolean).join(' ')
    // Kept apart from the display name: the email greets by first name only.
    row.customer_first_name = customer.first_name || ''
    // The ledger selects u.username AS email, so the address arrives as `email`.
    // The username fallback is there in case a future query stops aliasing it -
    // reading the wrong one of these is what stopped the old panel's payment
    // confirmations from ever sending.
    row.customer_email = customer.email || customer.username || ''
    // Past-due penalty already folded into current_balance_due - shown so the
    // operator can see why the balance is higher than the fees alone.
    row.interest = Number(customer.interest_applied) || 0
    row.amount_owed = Number(customer.current_balance_due) || 0
    // The row reconciled - we found the customer and their balance. Any issue
    // raised below is about the check, not the lookup.
    row.reconciled = true
    checkRow(row, customer)
  }

  // Remove Interest ------------------------------------------------------------
  // Deletes the account's past-due penalty rows server-side - irreversible - then
  // re-reads the account so the balance on screen comes from the ledger rather
  // than local arithmetic. Every row on the same account is refreshed, since more
  // than one check can be entered against it.
  $scope.removeInterest = async function (row) {
    row.confirmInterest = false
    if (!row.user_id || !row.interest) return

    const user_id = row.user_id
    const affected = $scope.enteredRows().filter(function (r) { return r.user_id === user_id })
    affected.forEach(function (r) { r.removingInterest = true })
    safeApply($scope)

    try {
      const results = await accountingService.removeInterest({ user_id: user_id })
      if (!results.data.success) {
        console.error('removeInterest failed:', results.data.error)
        alert('Could not remove the interest. Nothing was changed.')
        return
      }

      const customer = await lookupCustomer(user_id, null)
      affected.forEach(function (r) { applyCustomer(r, customer) })
    } catch (error) {
      console.error('Error removing interest:', error)
      alert('Could not remove the interest. Nothing was changed.')
    } finally {
      affected.forEach(function (r) { r.removingInterest = false })
      safeApply($scope)
    }
  }

  // Reconcile ------------------------------------------------------------------
  // Fills "User's Name" and "Amount Owed" from the customer record behind each
  // User ID, then runs the rules above. getGoldClients takes one id per call, so
  // rows are looked up one at a time and repeated ids are served from a per-pass
  // cache.
  $scope.reconcile = async function () {
    const rows = $scope.enteredRows()
    if (!rows.length) return

    $scope.reconciling = true
    safeApply($scope)

    const cache = {}

    try {
      for (const row of rows) {
        row.issues = []
        // A fresh pass re-raises every block: a waiver has to be re-affirmed
        // against the rules as they now stand, not carried over blind.
        row.override = false

        if (!row.user_id) {
          row.issues.push(issue('Missing User ID', 'account', false))
          row.reconciled = false
          continue
        }

        try {
          applyCustomer(row, await lookupCustomer(row.user_id, cache))
        } catch (error) {
          console.error('Error reconciling row:', row, error)
          row.reconciled = false
          row.issues.push(issue('Lookup failed', 'account', false))
        }
      }
    } finally {
      // checkRow evaluated each row against the register as it stood at that
      // moment; this settles the pairs once every row has been through.
      refreshRowRelations()
      $scope.reconciling = false
      safeApply($scope)
    }
  }

  // Post Checks ----------------------------------------------------------------
  // Any issue stops the batch until that row is corrected or overridden.
  $scope.blockedCount = function () {
    return $scope.enteredRows().filter(isBlocked).length
  }

  $scope.overriddenCount = function () {
    return $scope.enteredRows().filter(function (row) { return row.override }).length
  }

  // Rows still waiting to go out. A posted row is excluded for good, so a second
  // click of Post Checks can never post it twice.
  function pendingRows() {
    return $scope.enteredRows().filter(function (row) { return !row.posted })
  }
  $scope.pendingRows = pendingRows

  $scope.postedCount = function () {
    return $scope.enteredRows().filter(function (row) { return row.posted }).length
  }

  $scope.canPost = function () {
    const rows = pendingRows()
    if (!rows.length) return false
    if ($scope.reconciling || $scope.posting) return false
    return rows.every(function (row) {
      if (isBlocked(row)) return false
      // An unreconciled row can only go out on an explicit override.
      if (!row.reconciled && !row.override) return false
      // Hard floor: there is no such thing as posting a zero-amount check, and
      // the issue that raises it is not overridable, so the two agree.
      return Number(row.amount) > 0
    })
  }

  // Groups the pending rows by account, preserving register order within each.
  function groupByAccount(rows) {
    const groups = []
    const byKey = {}
    rows.forEach(function (row) {
      const key = accountKey(row)
      if (!byKey[key]) {
        byKey[key] = { key: key, rows: [] }
        groups.push(byKey[key])
      }
      byKey[key].rows.push(row)
    })
    return groups
  }

  $scope.postChecks = async function () {
    if (!$scope.canPost()) return

    const rows = pendingRows()
    const groups = groupByAccount(rows)
    const total = rows.reduce(function (sum, r) { return sum + (Number(r.amount) || 0) }, 0)
    const no_address = groups.filter(function (g) { return !g.rows[0].customer_email }).length
    const multi = groups.filter(function (g) { return g.rows.length > 1 }).length

    const confirmation = await Swal.fire({
      title: `Post ${rows.length} check${rows.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left">`
        + `<div><strong>${$filter('currency')(total, '$', 2)}</strong> across `
        + `<strong>${groups.length}</strong> account${groups.length === 1 ? '' : 's'}.</div>`
        + `<div style="margin-top:10px">${groups.length} confirmation email`
        + `${groups.length === 1 ? '' : 's'} will be sent`
        + (multi
          ? ` - the ${multi} account${multi === 1 ? '' : 's'} with several checks `
            + `get${multi === 1 ? 's' : ''} one email listing all of them.`
          : '.')
        + `</div>`
        + (no_address
          ? `<div style="margin-top:10px;color:#b42318"><strong>${no_address}</strong> `
            + `account${no_address === 1 ? ' has' : 's have'} no email on file and will post `
            + `without a confirmation.</div>`
          : '')
        + `<div style="margin-top:10px">This cannot be undone.</div></div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#12a150',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, post ${rows.length === 1 ? 'it' : 'them'}`
    })
    if (!confirmation.isConfirmed) return

    $scope.posting = true
    $scope.postProgress = 0
    safeApply($scope)

    let posted = 0
    let failed = 0
    let unsent = 0

    try {
      /*
        One request per ACCOUNT, not per check. The server posts each check as
        its own payment row and then sends a single confirmation covering them,
        so a customer who sent three checks in one envelope gets one email
        listing all three rather than three near-identical ones.

        Sequential across accounts on purpose: there is no transaction spanning
        the batch, so if one account fails the ones before it must stay posted
        and the ones after it must stay untouched and retryable.
      */
      for (const group of groups) {
        const first = group.rows[0]
        group.rows.forEach(function (r) { r.post_error = '' })

        try {
          const results = await accountingService.postCheckBatch({
            user_id: first.user_id,
            recipient: first.customer_email,
            name: first.customer_first_name || first.customer_name || '',
            previous_balance: first.amount_owed,
            payment_date: moment().format('YYYY-MM-DD'),
            send_email: true,
            checks: group.rows.map(function (r) {
              return { check_number: r.check_number, amount: Number(r.amount) }
            })
          })

          const data = results.data || {}
          // Match on check number: the server reports which ones reached disk.
          const landed = {}
          ;(data.posted || []).forEach(function (c) { landed[String(c.check_number)] = true })

          group.rows.forEach(function (row) {
            if (landed[String(row.check_number)]) {
              row.posted = true
              row.email_sent = !!data.email_sent
              row.email_error = data.email_error || ''
              if (!data.email_sent) unsent++
              posted++
            } else {
              row.post_error = 'Payment did not post'
              failed++
            }
            $scope.postProgress++
          })
        } catch (error) {
          console.error('Error posting checks for account:', first.user_id, error)
          group.rows.forEach(function (row) {
            row.post_error = 'Payment did not post'
            failed++
            $scope.postProgress++
          })
        }
        safeApply($scope)
      }
    } finally {
      $scope.posting = false
      safeApply($scope)
    }

    /*
      Clear only the rows that fully succeeded - the payment committed AND the
      confirmation went out. Anything that posted without an email, or failed to
      post at all, stays on screen: those are the ones still needing a decision,
      and dropping them would hide the exception among the successes.
    */
    const cleared = $scope.rows.filter(function (r) { return r.posted && r.email_sent }).length
    if (cleared) {
      $scope.rows = $scope.rows.filter(function (r) { return !(r.posted && r.email_sent) })
      if (!$scope.rows.length) $scope.rows.push(blankRow())
      appendTrailingBlank()
      // Clearing rows can resolve a duplicate, and changes what is grouped.
      refreshRowRelations()
      safeApply($scope)
    }

    const lines = [`${posted} of ${rows.length} check${rows.length === 1 ? '' : 's'} posted`
      + ` across ${groups.length} account${groups.length === 1 ? '' : 's'}.`]
    if (cleared) lines.push(`${cleared} posted and emailed - cleared from the register.`)
    if (unsent) lines.push(`${unsent} posted without a confirmation email - left on the register.`)
    if (failed) lines.push(`${failed} did not post - left on the register to retry.`)

    Swal.fire({
      title: failed ? 'Posted with errors' : 'Checks posted',
      html: lines.join('<br>'),
      icon: failed ? 'error' : (unsent ? 'warning' : 'success')
    })
  }

})
